/**
 * V68 인력 관리 — Tab 3: 고용형태 정보 hook
 *
 * read: getWorkforceEmploymentTypeInfo callable
 * write: updateWorkforceEmploymentTypeVisibility callable
 *
 * 응답 + canEditVisibility 별도 보유. refetch 함수 제공.
 */
import { useEffect, useState, useCallback } from 'react';
import { httpsCallable } from 'firebase/functions';
import { getFunctions } from '../../firebase/utils';

export type CompanyCode = 'KR' | 'CN';

export interface EmpTypeCounts {
  regular: number;
  intern: number;
  dispatch: number;
  contract: number;
  total: number;
}

export interface OrgRow {
  orgUnitId: string;
  orgUnitName: string;
  regular: number;
  intern: number;
  dispatch: number;
  contract: number;
  total: number;
}

export interface CompanySection {
  rows: OrgRow[];
  summary: EmpTypeCounts;
  visibility: Record<string, boolean>;
  canEditVisibility: boolean;
}

export interface AvailableOrg {
  orgUnitId: string;
  name: string;
}

export interface EmploymentTypeInfoResponse {
  success: boolean;
  scope: { countries: CompanyCode[]; canChooseAll: boolean };
  kr?: CompanySection;
  cn?: CompanySection;
  availableOrgs: { kr: AvailableOrg[]; cn: AvailableOrg[] };
  error?: string;
}

export function useWorkforceEmploymentTypeInfo() {
  const [data, setData] = useState<EmploymentTypeInfoResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  const refetch = useCallback(() => setReloadKey(k => k + 1), []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const fn = httpsCallable<unknown, EmploymentTypeInfoResponse>(
          getFunctions(), 'getWorkforceEmploymentTypeInfo'
        );
        const r = await fn({});
        if (cancelled) return;
        if (r.data.success) {
          setData(r.data);
        } else {
          setError(r.data.error || '조회 실패');
        }
      } catch (e: any) {
        if (!cancelled) setError(e?.message || '서버 오류');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [reloadKey]);

  const updateVisibility = useCallback(async (
    companyCode: CompanyCode,
    orgUnitId: string,
    visible: boolean,
  ): Promise<{ success: boolean; error?: string }> => {
    try {
      const fn = httpsCallable<
        { companyCode: CompanyCode; orgUnitId: string; visible: boolean },
        { success: boolean }
      >(getFunctions(), 'updateWorkforceEmploymentTypeVisibility');
      const r = await fn({ companyCode, orgUnitId, visible });
      if (r.data.success) {
        refetch();
        return { success: true };
      }
      return { success: false, error: '저장 실패' };
    } catch (e: any) {
      return { success: false, error: e?.message || '서버 오류' };
    }
  }, [refetch]);

  return { data, loading, error, refetch, updateVisibility };
}
